import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { 
  Users, 
  FileText, 
  DollarSign, 
  Building2,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  TrendingUp
} from 'lucide-react';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const API = `${process.env.REACT_APP_BACKEND_URL || '/_/backend'}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [applications, setApplications] = useState([]);
  const [lenders, setLenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('leads');
  
  // Modal states
  const [showLenderModal, setShowLenderModal] = useState(false);
  const [editingLender, setEditingLender] = useState(null);
  const [lenderForm, setLenderForm] = useState({
    name: '',
    min_rate: '',
    max_rate: '',
    min_amount: '5000',
    max_amount: '100000',
    min_term: '12',
    max_term: '84',
    establishment_fee: '0',
    monthly_fee: '0',
    features: ''
  });
  const [savingLender, setSavingLender] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch user
        const userRes = await fetch(`${API}/auth/me`, { credentials: 'include' });
        if (userRes.ok) {
          const userData = await userRes.json();
          if (userData.role !== 'admin') {
            navigate('/dashboard');
            return;
          }
          setUser(userData);
        }

        // Fetch stats
        const statsRes = await fetch(`${API}/admin/stats`, { credentials: 'include' });
        if (statsRes.ok) {
          const statsData = await statsRes.json();
          setStats(statsData);
        }

        // Fetch applications
        const appsRes = await fetch(`${API}/applications/all`, { credentials: 'include' });
        if (appsRes.ok) {
          const appsData = await appsRes.json();
          setApplications(appsData);
        }

        // Fetch lenders
        const lendersRes = await fetch(`${API}/lenders/all`, { credentials: 'include' });
        if (lendersRes.ok) {
          const lendersData = await lendersRes.json();
          setLenders(lendersData);
        }
      } catch (e) {
        console.error('Error fetching admin data:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-NZ', {
      style: 'currency',
      currency: 'NZD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-NZ', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const config = {
      scored: { icon: Clock, className: 'bg-gray-100 text-gray-600', label: 'Scored' },
      submitted: { icon: Clock, className: 'bg-amber-100 text-amber-700', label: 'Submitted' },
      pending: { icon: Clock, className: 'bg-amber-100 text-amber-700', label: 'Pending' },
      reviewing: { icon: AlertCircle, className: 'bg-blue-100 text-blue-700', label: 'Reviewing' },
      approved: { icon: CheckCircle, className: 'bg-[#00D47E]/10 text-gray-700', label: 'Approved' },
      conditionally_approved: { icon: CheckCircle, className: 'bg-[#00D47E]/10 text-gray-700', label: 'Conditional' },
      referred: { icon: AlertCircle, className: 'bg-blue-100 text-blue-700', label: 'Referred' },
      declined: { icon: XCircle, className: 'bg-red-100 text-red-700', label: 'Declined' },
      completed: { icon: CheckCircle, className: 'bg-purple-100 text-purple-700', label: 'Completed' }
    };
    const { icon: Icon, className, label } = config[status] || config.pending;
    
    return (
      <Badge className={`${className} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {label}
      </Badge>
    );
  };

  const handleStatusChange = async (applicationId, newStatus) => {
    try {
      const res = await fetch(`${API}/applications/${applicationId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });

      if (res.ok) {
        setApplications(prev => prev.map(app => 
          app.application_id === applicationId ? { ...app, status: newStatus } : app
        ));
        toast.success('Status updated');
      } else {
        toast.error('Failed to update status');
      }
    } catch (e) {
      console.error('Error updating status:', e);
      toast.error('Failed to update status');
    }
  };

  const openLenderModal = (lender = null) => {
    if (lender) {
      setEditingLender(lender);
      setLenderForm({
        name: lender.name,
        min_rate: lender.min_rate.toString(),
        max_rate: lender.max_rate.toString(),
        min_amount: lender.min_amount.toString(),
        max_amount: lender.max_amount.toString(),
        min_term: lender.min_term.toString(),
        max_term: lender.max_term.toString(),
        establishment_fee: lender.establishment_fee.toString(),
        monthly_fee: lender.monthly_fee.toString(),
        features: lender.features?.join(', ') || ''
      });
    } else {
      setEditingLender(null);
      setLenderForm({
        name: '',
        min_rate: '',
        max_rate: '',
        min_amount: '5000',
        max_amount: '100000',
        min_term: '12',
        max_term: '84',
        establishment_fee: '0',
        monthly_fee: '0',
        features: ''
      });
    }
    setShowLenderModal(true);
  };

  const handleSaveLender = async () => {
    if (!lenderForm.name || !lenderForm.min_rate || !lenderForm.max_rate) {
      toast.error('Please fill in required fields');
      return;
    }

    setSavingLender(true);
    try {
      const payload = {
        name: lenderForm.name,
        min_rate: parseFloat(lenderForm.min_rate),
        max_rate: parseFloat(lenderForm.max_rate),
        min_amount: parseFloat(lenderForm.min_amount),
        max_amount: parseFloat(lenderForm.max_amount),
        min_term: parseInt(lenderForm.min_term),
        max_term: parseInt(lenderForm.max_term),
        establishment_fee: parseFloat(lenderForm.establishment_fee),
        monthly_fee: parseFloat(lenderForm.monthly_fee),
        features: lenderForm.features.split(',').map(f => f.trim()).filter(Boolean)
      };

      const url = editingLender 
        ? `${API}/lenders/${editingLender.lender_id}`
        : `${API}/lenders`;
      
      const res = await fetch(url, {
        method: editingLender ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const lenderData = await res.json();
        if (editingLender) {
          setLenders(prev => prev.map(l => l.lender_id === lenderData.lender_id ? lenderData : l));
          toast.success('Lender updated');
        } else {
          setLenders(prev => [...prev, lenderData]);
          toast.success('Lender created');
        }
        setShowLenderModal(false);
      } else {
        toast.error('Failed to save lender');
      }
    } catch (e) {
      console.error('Error saving lender:', e);
      toast.error('Failed to save lender');
    } finally {
      setSavingLender(false);
    }
  };

  const handleDeleteLender = async (lenderId) => {
    if (!window.confirm('Are you sure you want to delete this lender?')) return;

    try {
      const res = await fetch(`${API}/lenders/${lenderId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        setLenders(prev => prev.filter(l => l.lender_id !== lenderId));
        toast.success('Lender deleted');
      } else {
        toast.error('Failed to delete lender');
      }
    } catch (e) {
      console.error('Error deleting lender:', e);
      toast.error('Failed to delete lender');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 animate-spin text-[#00D47E]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Header */}
      <section className="pt-24 pb-8 md:pt-32 md:pb-12 bg-gradient-to-br from-black via-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-outfit text-3xl md:text-4xl font-bold text-white mb-2">
              Admin Dashboard
            </h1>
            <p className="text-gray-400">
              Manage leads, lenders, and applications
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      {stats && (
        <section className="py-8 -mt-4">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                { label: 'Total Leads', value: stats.total_applications, icon: FileText, color: 'bg-[#00D47E]/10 text-[#00D47E]' },
                { label: 'Pending', value: stats.pending_applications, icon: Clock, color: 'bg-amber-100 text-amber-600' },
                { label: 'Approved', value: stats.approved_applications, icon: CheckCircle, color: 'bg-[#00D47E]/10 text-[#00D47E]' },
                { label: 'Users', value: stats.total_users, icon: Users, color: 'bg-blue-100 text-blue-600' },
                { label: 'Loan Value', value: formatCurrency(stats.total_loan_value), icon: DollarSign, color: 'bg-purple-100 text-purple-600' }
              ].map((stat, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="bg-white rounded-xl border border-gray-200 p-4"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.color}`}>
                      <stat.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{stat.label}</p>
                      <p className="font-outfit text-lg font-bold text-black">{stat.value}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Main Content */}
      <section data-testid="admin-content" className="py-8">
        <div className="max-w-7xl mx-auto px-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="leads" data-testid="leads-tab">
                <FileText className="w-4 h-4 mr-2" />
                Leads
              </TabsTrigger>
              <TabsTrigger value="lenders" data-testid="lenders-tab">
                <Building2 className="w-4 h-4 mr-2" />
                Lenders
              </TabsTrigger>
            </TabsList>

            {/* Leads Tab */}
            <TabsContent value="leads">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="font-outfit text-lg font-bold text-black">
                    All Applications ({applications.length})
                  </h2>
                </div>

                {applications.length === 0 ? (
                  <div className="p-12 text-center text-gray-500">
                    No applications yet
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Customer</TableHead>
                          <TableHead>Vehicle</TableHead>
                          <TableHead>Lender</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {applications.map((app) => (
                          <TableRow key={app.application_id} data-testid={`admin-app-${app.application_id}`}>
                            <TableCell className="text-sm text-gray-600">
                              {formatDate(app.created_at)}
                            </TableCell>
                            {/* GTM v1 leads are anonymous: no user_id, no lender, no vehicle
                                detail. Identify them by phone and show what was captured, rather
                                than blank columns and a misleading $0/wk. */}
                            <TableCell>
                              <p className="font-medium text-black">
                                {app.phone || (app.user_id ? app.user_id.slice(-8) : app.application_id.slice(-8))}
                              </p>
                              <p className="text-xs text-gray-500">
                                {app.employment_type || app.employment_status || (app.user_id ? '—' : 'Anonymous lead')}
                              </p>
                            </TableCell>
                            <TableCell>
                              <p className="text-sm text-black">
                                {app.vehicle_make
                                  ? `${app.vehicle_year || ''} ${app.vehicle_make} ${app.vehicle_model || ''}`.trim()
                                  : app.vehicle_type || '—'}
                              </p>
                            </TableCell>
                            <TableCell className="font-medium">
                              {app.lender_name || (
                                app.approval_likelihood
                                  ? <span className="text-xs text-gray-500 capitalize">{app.approval_likelihood} likelihood{app.approval_score != null ? ` (${app.approval_score}%)` : ''}</span>
                                  : '—'
                              )}
                            </TableCell>
                            <TableCell>
                              <p className="font-medium">{formatCurrency(app.loan_amount)}</p>
                              {app.weekly_payment != null && (
                                <p className="text-xs text-[#00D47E]">{formatCurrency(app.weekly_payment)}/wk</p>
                              )}
                            </TableCell>
                            <TableCell>{getStatusBadge(app.status)}</TableCell>
                            <TableCell>
                              <Select 
                                value={app.status} 
                                onValueChange={(val) => handleStatusChange(app.application_id, val)}
                              >
                                <SelectTrigger className="w-32" data-testid={`status-select-${app.application_id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="scored">Scored</SelectItem>
                                  <SelectItem value="submitted">Submitted</SelectItem>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="reviewing">Reviewing</SelectItem>
                                  <SelectItem value="approved">Approved</SelectItem>
                                  <SelectItem value="conditionally_approved">Conditional</SelectItem>
                                  <SelectItem value="referred">Referred</SelectItem>
                                  <SelectItem value="declined">Declined</SelectItem>
                                  <SelectItem value="completed">Completed</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Lenders Tab */}
            <TabsContent value="lenders">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="font-outfit text-lg font-bold text-black">
                    Lenders ({lenders.length})
                  </h2>
                  <Button
                    onClick={() => openLenderModal()}
                    data-testid="add-lender-btn"
                    className="bg-black hover:bg-gray-900 rounded-full"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Lender
                  </Button>
                </div>

                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Rate Range</TableHead>
                        <TableHead>Loan Range</TableHead>
                        <TableHead>Term</TableHead>
                        <TableHead>Fees</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lenders.map((lender) => (
                        <TableRow key={lender.lender_id} data-testid={`admin-lender-${lender.lender_id}`}>
                          <TableCell className="font-medium">{lender.name}</TableCell>
                          <TableCell>{lender.min_rate}% - {lender.max_rate}%</TableCell>
                          <TableCell>{formatCurrency(lender.min_amount)} - {formatCurrency(lender.max_amount)}</TableCell>
                          <TableCell>{lender.min_term} - {lender.max_term} mo</TableCell>
                          <TableCell>
                            <p className="text-sm">Setup: {formatCurrency(lender.establishment_fee)}</p>
                            <p className="text-xs text-gray-500">Monthly: {formatCurrency(lender.monthly_fee)}</p>
                          </TableCell>
                          <TableCell>
                            <Badge className={lender.is_active ? 'bg-[#00D47E]/10 text-gray-700' : 'bg-gray-100 text-gray-600'}>
                              {lender.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => openLenderModal(lender)}
                                data-testid={`edit-lender-${lender.lender_id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDeleteLender(lender.lender_id)}
                                data-testid={`delete-lender-${lender.lender_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Lender Modal */}
      <Dialog open={showLenderModal} onOpenChange={setShowLenderModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit">
              {editingLender ? 'Edit Lender' : 'Add New Lender'}
            </DialogTitle>
            <DialogDescription>
              {editingLender ? 'Update lender details' : 'Add a new lender to the platform'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="name">Lender Name *</Label>
              <Input
                id="name"
                data-testid="lender-name-input"
                value={lenderForm.name}
                onChange={(e) => setLenderForm(prev => ({ ...prev, name: e.target.value }))}
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="min_rate">Min Rate (%) *</Label>
                <Input
                  id="min_rate"
                  type="number"
                  step="0.01"
                  data-testid="min-rate-input"
                  value={lenderForm.min_rate}
                  onChange={(e) => setLenderForm(prev => ({ ...prev, min_rate: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="max_rate">Max Rate (%) *</Label>
                <Input
                  id="max_rate"
                  type="number"
                  step="0.01"
                  data-testid="max-rate-input"
                  value={lenderForm.max_rate}
                  onChange={(e) => setLenderForm(prev => ({ ...prev, max_rate: e.target.value }))}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="min_amount">Min Loan ($)</Label>
                <Input
                  id="min_amount"
                  type="number"
                  value={lenderForm.min_amount}
                  onChange={(e) => setLenderForm(prev => ({ ...prev, min_amount: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="max_amount">Max Loan ($)</Label>
                <Input
                  id="max_amount"
                  type="number"
                  value={lenderForm.max_amount}
                  onChange={(e) => setLenderForm(prev => ({ ...prev, max_amount: e.target.value }))}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="establishment_fee">Setup Fee ($)</Label>
                <Input
                  id="establishment_fee"
                  type="number"
                  value={lenderForm.establishment_fee}
                  onChange={(e) => setLenderForm(prev => ({ ...prev, establishment_fee: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="monthly_fee">Monthly Fee ($)</Label>
                <Input
                  id="monthly_fee"
                  type="number"
                  value={lenderForm.monthly_fee}
                  onChange={(e) => setLenderForm(prev => ({ ...prev, monthly_fee: e.target.value }))}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="features">Features (comma-separated)</Label>
              <Input
                id="features"
                data-testid="features-input"
                placeholder="Feature 1, Feature 2, Feature 3"
                value={lenderForm.features}
                onChange={(e) => setLenderForm(prev => ({ ...prev, features: e.target.value }))}
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLenderModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveLender}
              disabled={savingLender}
              data-testid="save-lender-btn"
              className="bg-black hover:bg-gray-900"
            >
              {savingLender ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Lender'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
